"use client";

import { COMPANY_EMAILS } from "@repo/common";
import { ChevronDownIcon } from "lucide-react";
import Link from "next/link";
import { useState } from "react";

import { cn } from "@/lib/utils";

import { faqs as defaultFaqs } from "./faq-data";

interface FaqEntry {
  question: string;
  answer: string;
}

export default function FAQs({
  faqs = defaultFaqs,
}: {
  /** FAQ entries to render. Defaults to the canonical marketing list. */
  faqs?: FaqEntry[];
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="mx-auto max-w-[1300px] px-4 py-16">
      <div className="flex flex-col gap-8">
        {/* Header */}
        <div className="flex flex-col gap-4">
          <h2 className="text-marketing-text dark:text-marketing-cream text-3xl leading-[1.1] font-medium tracking-[-1.5px] md:text-4xl lg:text-[50px]">
            FAQ
          </h2>
          <p className="text-marketing-text dark:text-marketing-cream text-base leading-[1.4] font-normal md:text-lg lg:text-xl">
            Question not answered? Email us at{" "}
            <Link
              href={`mailto:${COMPANY_EMAILS.support}`}
              className="underline"
            >
              {COMPANY_EMAILS.support}
            </Link>{" "}
            or book a meeting with our team.
          </p>
          <Link
            href="https://cal.com/zac/terac"
            className="bg-marketing-blue flex w-fit items-center gap-2 rounded-full px-5 py-3 text-sm font-medium text-white"
          >
            Book a meeting
            <ArrowRightIcon />
          </Link>
        </div>

        {/* FAQ Items */}
        <div className="flex flex-col">
          {faqs.map((faq, index) => (
            <div key={faq.question} className="flex flex-col">
              {/* Question row */}
              <button
                onClick={() => {
                  setOpenIndex(openIndex === index ? null : index);
                }}
                aria-expanded={openIndex === index}
                aria-controls={`faq-answer-${index}`}
                className="flex w-full items-center justify-between py-6 text-left"
              >
                <span className="text-marketing-text dark:text-marketing-cream text-base leading-[1.4] font-medium md:text-lg lg:text-xl">
                  {faq.question}
                </span>
                <div
                  className={cn(
                    "flex h-[18px] w-[18px] shrink-0 items-center justify-center transition-transform",
                    openIndex === index && "rotate-180",
                  )}
                >
                  <ChevronDownIcon className="text-marketing-text dark:text-marketing-cream h-[18px] w-[18px]" />
                </div>
              </button>

              {/* Answer - always in DOM for SEO, toggled with CSS */}
              <div
                id={`faq-answer-${index}`}
                role="region"
                className={cn(
                  "grid transition-[grid-template-rows,padding-bottom] duration-200 ease-in-out",
                  openIndex === index
                    ? "grid-rows-[1fr] pb-6"
                    : "grid-rows-[0fr]",
                )}
              >
                <div className="overflow-hidden">
                  <p className="text-marketing-text dark:text-marketing-cream max-w-[619px] text-base leading-[1.4] font-normal">
                    {faq.answer}
                  </p>
                </div>
              </div>

              {/* Divider */}
              {index < faqs.length - 1 && (
                <div className="bg-marketing-border h-px w-full dark:bg-white/20" />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ArrowRightIcon() {
  return (
    <svg
      width="18"
      height="12"
      viewBox="0 0 18 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1 6H17M17 6L12 1M17 6L12 11"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
